// ABOUTME: Simplified editor container for the Plus Load shortcut flow.
// Shows only: quantity field → final review → result (no overview screen).
import {usePlusLoad} from '@hconnect/tuc-processes/orderManagement/orderChange/plusLoad/usePlusLoad'
import React, {useState, useCallback} from 'react'

import {useEditorState} from '../../../shared/order-editor/hooks/useEditorState'
import {OrderChangeResultType} from '../../types/orderChangeResult.types'
import {FinalReview} from '../finalReview/FinalReview'
import {OrderChangeResult} from '../OrderChangeResult/OrderChangeResult'

import {FieldEditorView} from './fieldEditorView/FieldEditorView'

type PlusLoadCustomState = 'result'

interface PlusLoadEditorContainerProps {
  onClose?: () => void
  jobSiteInfo?: string
}

export const PlusLoadEditorContainer: React.FC<PlusLoadEditorContainerProps> = ({
  onClose,
  jobSiteInfo
}) => {
  const {state, goToField, goToFinalReview, goToState, currentFieldId} =
    useEditorState<PlusLoadCustomState>({type: 'field', fieldId: 'quantity'})

  const [resultType, setResultType] = useState<OrderChangeResultType | null>(null)

  // Disable "Select & Continue" when plusLoadQuantity is 0 (nothing to add)
  const plusLoadData = usePlusLoad()
  const isPlusLoadQuantityZero = plusLoadData.isEnabled
    ? (plusLoadData.plusLoadQuantity ?? 0) === 0
    : false

  // Back from quantity → close the side panel entirely
  const handleBack = () => {
    onClose?.()
  }

  // "Select and Continue" → go straight to final review
  const handleSelectAndContinue = () => {
    goToFinalReview()
  }

  // Back from final review → return to quantity selection
  const handleReviewBack = () => {
    goToField('quantity')
  }

  const handleShowResult = useCallback(
    (type: OrderChangeResultType) => {
      setResultType(type)
      goToState('result')
    },
    [goToState]
  )

  const handleViewOrderDetails = () => {
    onClose?.()
  }

  // Quantity field step
  if (state.type === 'field' && currentFieldId) {
    return (
      <FieldEditorView
        fieldId={currentFieldId}
        onBack={handleBack}
        onSelectAndContinue={handleSelectAndContinue}
        goToField={goToField}
        isSelectAndContinueDisabled={isPlusLoadQuantityZero}
      />
    )
  }

  // Final review
  if (state.type === 'finalReview') {
    return (
      <FinalReview
        onBack={handleReviewBack}
        onClose={onClose}
        jobSiteInfo={jobSiteInfo}
        onSubmitSuccess={handleShowResult}
      />
    )
  }

  // Result screen
  if (state.type === 'result' && resultType) {
    return (
      <OrderChangeResult
        resultType={resultType}
        onViewOrderDetails={handleViewOrderDetails}
        onClose={onClose}
        jobSiteInfo={jobSiteInfo}
      />
    )
  }

  // Fallback — should not happen
  return null
}
